import { AfterViewInit, Component, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { BehaviorSubject, Observable, Subscription, combineLatest, forkJoin, map, of, switchMap, take } from 'rxjs';
import * as uuid from 'uuid';

import { AgentAdhocSourceResponse, CurationAgentSourceStatusEnum } from '../../../api/agents/models/curation-agent.api';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { TableColumnFormatsEnum } from '../../../Common/Forms/enums/table-column-formats.enum';
import { TableColumnInputEnum } from '../../../Common/Forms/enums/table-column-input-type.enum';
import { TableColumnModel } from '../../../Common/Forms/models/table-column.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { TakeOverPanelComponent } from '../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { getAllDateQueryOptions } from '../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { AgentRouteSectionEnum, agentSectionRoute } from '../../AutomationAgentsRouter/automation-agents-route.const';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { CurationAgentSourceModel } from '../../models/curation-agent-source.model';
import { CurationAgentsService } from '../../services/curation-agents.service';
import { AdHocReportPanelComponent } from '../AdHocReportPanel/ad-hoc-report-panel.component';
import { CurationAgentPanelComponent } from '../CurationAgentPanel/curation-agent-panel.component';
import {
    AgentSourcePanelModeEnum,
    AgentSourcePanelComponent
} from '../AgentSourcePanel/agent-source-panel.component';

/** One agent watching this source, with what it has done over the chosen window */
interface LiveAgentRow {
    agent: CurationAgentModel;
    source: CurationAgentSourceModel;
    analyzed: number;
    curated: number;
    /** Flattened for the table, which sorts and renders on plain row fields */
    agentName: string;
    typeLabel: string;
    activity: string;
    activitySort: number;
    /** On for standby too - it is still switched on, only parked for quota */
    isActive: boolean;
}

/** One ad hoc run by an agent that watches this source */
interface AdHocRow {
    run: AgentAdhocSourceResponse;
    agent: CurationAgentModel;
    createdAt: number;
    agentName: string;
    typeLabel: string;
    activity: string;
    activitySort: number;
}

/**
 * Every automation agent working on one source - the topic, folder, theme or subscription the
 * panel was opened from - with the ad hoc runs those agents have made.
 *
 * A source with nothing on it yet gets the panel's own first-run state instead of two empty tables.
 */
@Component({
    selector: 'agpr-manage-source-agents-panel',
    templateUrl: './manage-source-agents-panel.component.html',
    styleUrls: ['./manage-source-agents-panel.component.scss'],
    standalone: false
})
export class ManageSourceAgentsPanelComponent extends AbstractDialogComponent
    implements OnInit, AfterViewInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly rangeOptions: DropdownOptionModel[] = getAllDateQueryOptions();

    /** Panel heading per source type - "Manage Topic Agents" and its siblings */
    private readonly titles: Partial<Record<MonitoringSeriesTypeEnum, string>> = {
        [MonitoringSeriesTypeEnum.topic]: 'AutomationAgents.manageSourceAgents.title.topic',
        [MonitoringSeriesTypeEnum.folder]: 'AutomationAgents.manageSourceAgents.title.folder',
        [MonitoringSeriesTypeEnum.tag]: 'AutomationAgents.manageSourceAgents.title.tag',
        [MonitoringSeriesTypeEnum.subscription]: 'AutomationAgents.manageSourceAgents.title.subscription',
        [MonitoringSeriesTypeEnum.theme]: 'AutomationAgents.manageSourceAgents.title.theme',
        [MonitoringSeriesTypeEnum.socialTopic]: 'AutomationAgents.manageSourceAgents.title.socialTopic',
        [MonitoringSeriesTypeEnum.sourceGroup]: 'AutomationAgents.manageSourceAgents.title.sourceGroup'
    };
    /** Status and Date lead their tables at the same width, so the Agent columns below them line up */
    private readonly firstColumnWidth: number = 200;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    /** The source the panel was opened from, as an agent stores it */
    @Input() sourceType: MonitoringSeriesTypeEnum;
    @Input() sourceId: number;
    /** The filters applied where the panel was opened from, handed on to the agent put on the source */
    @Input() filterSearchCriteria: MentionSearchCriteriaModel;

    @ViewChild('takeOverPanel') takeOverPanel: TakeOverPanelComponent;
    @ViewChild('statusCell') statusCell: TemplateRef<any>;
    @ViewChild('agentCell') agentCell: TemplateRef<any>;

    hideSlideOverlay: boolean = false;
    loading: boolean = true;

    /** The window the Activity columns report over */
    selectedRange: DateQueryModel = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_24_HOURS);

    liveAgents: LiveAgentRow[] = [];
    adHocHistory: AdHocRow[] = [];
    /** Whether the account has any agents at all, which this source could be added to */
    hasAccountAgents: boolean = false;

    /** Sorted on by default, and the column the agent link is drawn in */
    readonly liveAgentNameColumn: TableColumnModel = {
        heading: 'AutomationAgents.manageSourceAgents.agent',
        type: TableColumnInputEnum.custom,
        field: 'agentName',
        sortable: true,
        sortField: 'agentName',
        sortType: 'text'
    };

    readonly adHocDateColumn: TableColumnModel = {
        heading: 'AutomationAgents.agent.dashboard.dateColumn',
        field: 'createdAt',
        format: TableColumnFormatsEnum.date,
        sortable: true,
        sortField: 'createdAt',
        sortType: 'numeric',
        width: this.firstColumnWidth
    };

    /** Both tables report the same two facts about a row, so they share these definitions */
    readonly typeColumn: TableColumnModel = {
        heading: 'AutomationAgents.manageSourceAgents.type',
        field: 'typeLabel',
        sortable: true,
        sortField: 'typeLabel',
        sortType: 'text',
        width: 140
    };

    readonly activityColumn: TableColumnModel = {
        heading: 'AutomationAgents.manageSourceAgents.activity',
        field: 'activity',
        sortable: true,
        sortField: 'activitySort',
        sortType: 'numeric',
        width: 140,
        styleClass: 'agpr-manageSourceAgents-activityHeading',
        cellStyleClass: 'agpr-manageSourceAgents-activityCell'
    };

    liveAgentColumns: TableColumnModel[] = [];
    adHocColumns: TableColumnModel[] = [];

    private readonly subscriptions: Subscription[] = [];
    private loadSubscription: Subscription;

    constructor(
        public coreDialogService: CoreDialogService,
        private curationAgentsService: CurationAgentsService,
        private localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) private appBaseHref: string
    ) {
        super(coreDialogService, uuid.v4());
    }

    ngOnInit(): void {
        this.subscriptions.push(
            // Re-reads whenever an agent, a source or a run changes
            this.curationAgentsService.agents$.subscribe(() => this.load()),
            this.curationAgentsService.hasAgents$.subscribe(hasAgents => this.hasAccountAgents = hasAgents)
        );
    }

    /** Cell templates only exist once the view is up, so the columns are completed here */
    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.liveAgentNameColumn.customTemplate = this.agentCell;

            this.liveAgentColumns = [
                {
                    heading: 'AutomationAgents.card.column.status',
                    type: TableColumnInputEnum.custom,
                    field: 'isActive',
                    customTemplate: this.statusCell,
                    sortable: true,
                    sortField: 'isActive',
                    width: this.firstColumnWidth
                },
                this.liveAgentNameColumn,
                this.typeColumn,
                this.activityColumn
            ];

            // Its own instance, so sorting one table does not move the arrow on the other
            this.adHocColumns = [
                this.adHocDateColumn,
                { ...this.liveAgentNameColumn },
                this.typeColumn,
                this.activityColumn
            ];
        });
    }

    ngOnDestroy(): void {
        this.loadSubscription?.unsubscribe();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    get title(): string {
        return this.titles[this.sourceType] ?? 'AutomationAgents.manageSourceAgents.title.source';
    }

    /**
     * No agents on the account at all, so the panel offers the feature rather than empty tables. An
     * account with agents elsewhere gets the tables, so this source can be added to one of them.
     */
    get isEmpty(): boolean {
        return !this.loading && !this.hasAccountAgents;
    }

    rangeSelected(range: DateQueryModel): void {
        this.selectedRange = range;
        this.load();
    }

    /** Absolute link to an agent's dashboard, so the row can open it in its own tab */
    agentUrl(agent: CurationAgentModel): string {
        return agent?.id
            ? this.appBaseHref + agentSectionRoute(agent.id, AgentRouteSectionEnum.dashboard).slice(1)
            : null;
    }

    statusChanged(row: LiveAgentRow, active: boolean): void {
        row.source.status = active
            ? CurationAgentSourceStatusEnum.active
            : CurationAgentSourceStatusEnum.paused;
        row.isActive = active;

        this.curationAgentsService.setSourceStatus(row.agent, row.source, null, true).subscribe();
    }

    editAgent(agent: CurationAgentModel): void {
        if (!agent?.id) {
            return;
        }

        // The listed agent carries no actions, so it has to be reloaded in full first
        this.curationAgentsService.get(agent.id).pipe(take(1)).subscribe(loaded => this.openAgentPanel(loaded));
    }

    /** Runs an agent over this source by hand, rather than waiting for it to come round */
    runAdHoc(): void {
        this.openSourcePanel(AgentSourcePanelModeEnum.adHoc);
    }

    viewReport(row: AdHocRow): void {
        if (!row?.run) {
            return;
        }

        this.coreDialogService.open(AdHocReportPanelComponent, {
            inputs: {
                animationTrigger: this.newAnimationTrigger(),
                overlayContainer: this.overlayContainer,
                agent: row.agent,
                run: row.run
            }
        });
    }

    removeSource(row: LiveAgentRow): void {
        if (!row) {
            return;
        }

        this.curationAgentsService.removeSource(row.agent, row.source).subscribe(() => this.load());
    }

    /** Puts another agent on this source for good */
    addLiveAgent(): void {
        this.openSourcePanel(AgentSourcePanelModeEnum.liveAgent);
    }

    /** No agents to add this source to yet, so the first one is created from scratch */
    createAgent(): void {
        this.openAgentPanel();
    }

    /** The same panel in both modes - it only differs in what it asks for and what it estimates */
    private openSourcePanel(mode: AgentSourcePanelModeEnum): void {
        this.coreDialogService.open(AgentSourcePanelComponent, {
            inputs: {
                animationTrigger: this.newAnimationTrigger(),
                overlayContainer: this.overlayContainer,
                mode: mode,
                sourceType: this.sourceType,
                sourceId: this.sourceId,
                filterSearchCriteria: this.filterSearchCriteria,
                // An ad hoc run opens on an agent already here; a new live agent is picked by hand
                agent: mode === AgentSourcePanelModeEnum.adHoc ? this.liveAgents[0]?.agent : null
            }
        });
    }

    close(): void {
        if (this.takeOverPanel?.isPanelFullyOpened) {
            this.takeOverPanel.close();

            return;
        }

        this.closeDialog();
    }

    onVisibleChange(isVisible: boolean): void {
        if (!isVisible) {
            this.closeDialog();
        }
    }

    /** The agents on this source, what they have drawn over the window, and their ad hoc runs */
    private load(): void {
        this.loadSubscription?.unsubscribe();
        this.loadSubscription = this.curationAgentsService.agents$.pipe(
            switchMap(agents => {
                const matches = (agents ?? [])
                    .map(agent => ({ agent: agent, source: this.sourceOn(agent) }))
                    .filter(row => !!row.source);

                // combineLatest, not forkJoin - accountAdHocRuns() stays open so it can re-read on a refresh
                return combineLatest({
                    rows: matches.length
                        ? forkJoin(matches.map(match => this.rowFor(match.agent, match.source)))
                        : of([] as LiveAgentRow[]),
                    // Any agent can be run over this source by hand, not only the ones watching it live
                    runs: this.curationAgentsService.accountAdHocRuns().pipe(
                        map(runs => this.adHocRowsFor(runs, agents))
                    )
                });
            })
        ).subscribe(({ rows, runs }) => {
            this.liveAgents = rows;
            this.adHocHistory = runs;
            this.loading = false;
        });
    }

    /** This panel's source on an agent, or null where the agent is not watching it */
    private sourceOn(agent: CurationAgentModel): CurationAgentSourceModel {
        return (agent?.sources ?? []).find(source => source.dataSource?.type === this.sourceType
            && Number(source.dataSource?.id) === Number(this.sourceId)) ?? null;
    }

    private rowFor(agent: CurationAgentModel, source: CurationAgentSourceModel): Observable<LiveAgentRow> {
        return this.curationAgentsService.sourceTotals(agent.id, source.id, this.selectedRange).pipe(
            map(({ analyzed, curated }) => ({
                agent: agent,
                source: source,
                analyzed: analyzed,
                curated: curated,
                agentName: agent.name,
                typeLabel: this.curationLabel,
                activity: `${curated}/${analyzed}`,
                activitySort: curated,
                isActive: source.isActive || source.isStandby
            }))
        );
    }

    /** The runs submitted over this source, by agents still on the account */
    private adHocRowsFor(runs: AgentAdhocSourceResponse[], agents: CurationAgentModel[]): AdHocRow[] {
        const byId = new Map<number, CurationAgentModel>((agents ?? []).map(agent => [agent.id, agent]));

        return (runs ?? [])
            .filter(run => run.sourceType === this.sourceType
                && Number(run.sourceRef) === Number(this.sourceId)
                && byId.has(run.agentId))
            .map(run => this.adHocRow(run, byId.get(run.agentId)));
    }

    private adHocRow(run: AgentAdhocSourceResponse, agent: CurationAgentModel): AdHocRow {
        return {
            run: run,
            agent: agent,
            createdAt: run?.createdAt,
            agentName: agent?.name,
            typeLabel: this.curationLabel,
            activity: `${run?.curatedMentions ?? 0}/${run?.totalMentions ?? 0}`,
            activitySort: run?.curatedMentions ?? 0
        };
    }

    /** Both tables report curation runs, which is the only agent type there is so far */
    private get curationLabel(): string {
        return this.localizationService.transform('AutomationAgents.agent.summary.type.curation');
    }

    private openAgentPanel(agent?: CurationAgentModel): void {
        this.coreDialogService.open(CurationAgentPanelComponent, {
            inputs: {
                animationTrigger: this.newAnimationTrigger(),
                overlayContainer: this.overlayContainer,
                agent: agent
            }
        });
    }

    private newAnimationTrigger(): BehaviorSubject<FullProfileAnimationModel> {
        return new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
    }
}
